import { AxiosError, AxiosResponse } from 'axios';
import { observer } from 'mobx-react';
import React from 'react';
import { FailResponse, InternalServerError, responseUtils } from '../../domain';
import {
  arrayAny,
  HttpPromisePendingState,
  HttpPromiseRejectedState,
  HttpPromiseState,
  isNullOrUndefined,
  isObject,
  PromisePendingState,
  PromiseRejectedState,
  PromiseState
} from '../helpers';


const { isFailResponse, isInternalServerError } = responseUtils;

function renderPending(pending: HttpPromisePendingState<any> | PromisePendingState<any>,
  label: string, className: string | null = null)
  : React.ReactNode {
  return (
    <div className={"alert alert-info text-center " + (className ?? "")}>
      <p><b><i className="fas fa-spinner fa-spin" /> {label} </b></p>
    </div>
  );
}

function isAxiosError(error: any): error is AxiosError {
  return isObject(error) &&
    (error as AxiosError).isAxiosError === true;
}

function hasErrorResponse(error: AxiosError): error is AxiosError & { response: AxiosResponse } {
  return isObject(error.response);
}

function isForbidden(response: AxiosResponse) {
  return response.status === 403;
}

const ErrorTemplate: React.FC<{ label: string, className: string | null }> = (props) => {
  const children = props.children;
  
  return (<div className={"alert alert-danger text-center " + (props.className ?? "")}>
    <p><b> {props.label} </b></p>
    {
      !isNullOrUndefined(children) &&
      <hr />
    }
    {children}
  </div>);
};

function responseItemString(item: { Field: string, Message: string }) {
  if (isNullOrUndefined(item.Field) || item.Field === '')
    return item.Message;

  return `${item.Field} - ${item.Message}`;
}

function renderError(state: HttpPromiseRejectedState<any> | PromiseRejectedState<any>,
  label: string, className: string | null = null): React.ReactNode {
  const error = state.error;
  if (typeof error === 'string')
    return (
      <ErrorTemplate className={className} label={label}>
        <p>{error}</p>
      </ErrorTemplate>);

  if (isAxiosError(error) && hasErrorResponse(error)) {
    const response = error.response;

    if (isForbidden(response))
      return (
        <ErrorTemplate className={className} label={label}>
          <p> You are not authorized to perform this operation </p>
        </ErrorTemplate>
      );

    if (isFailResponse(response.data)) {
      const failResponse: FailResponse = response.data;
      return (
        <ErrorTemplate className={className} label={label}>
          {
            failResponse.Errors.map((x, index) => (
              <div key={index}>
                <p>{responseItemString(x)}</p>
                <p className="d-none"> {x.StackTrace}</p>
              </div>))
          }
        </ErrorTemplate>
      );
    }

    if (isInternalServerError(response.data)) {
      const internalServerError: InternalServerError = response.data;

      return (
        <ErrorTemplate className={className} label={label}>
          <p> Internal server error encountered. ({internalServerError.Timestamp}). </p>
          <p> Please notify site administrators </p>
          {arrayAny(internalServerError.StackTrace) &&
            <div className="d-none">
              {internalServerError.StackTrace.map(x => (<p>{x}</p>))}
            </div>}
        </ErrorTemplate>
      );
    }

  }

  if (error instanceof Error && !isNullOrUndefined(error.message))
    return (
      <ErrorTemplate className={className} label={label}>
        <p>{error.message}</p>
      </ErrorTemplate>);

  return (<ErrorTemplate className={className} label={label} />);
}

function render(state: PromiseState<any> | HttpPromiseState<any>,
  label: string, className: string | null = null): React.ReactNode {
  if (state.isPending)
    return renderPending(state, label, className);
  if (state.isRejected)
    return renderError(state, label, className);
  return null;
}

@observer
export class PromiseMessage extends React.Component<{
  label: string;
  state: PromiseState<any> | HttpPromiseState<any> | null;
  align: 'left' | 'center' | 'right' | 'fill' | 'nowrap';
  className?: string | null;
}> {
  render() {
    const { label, state, align, className } = this.props;

    if (state === null || state.isResolved)
      return null;

    switch (align) {
      case 'nowrap':
        return render(state, label, className);
      case 'left':
        return (
          <div className="row">
            <div className="col-auto">
              {render(state, label, className)}
            </div>
          </div>
        );
      case 'center':
        return (
          <div className="row justify-content-center">
            <div className="col-auto">
              {render(state, label, className)}
            </div>
          </div>
        );
      case 'right':
        return (
          <div className="row justify-content-end">
            <div className="col-auto">
              {render(state, label, className)}
            </div>
          </div>
        );
      case 'fill':
      default:
        return (
          <div className="row">
            <div className="col">
              {render(state, label, className)}
            </div>
          </div>
        );
    }
  }
}
